import { CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAppDispatch } from 'app/hooks';
import Button from 'components/inputs/button';
import AccessorySelectionModal from 'components/layout/modals/accessory-selection-modal';
import { clearDuctedPairPick } from 'features/ui';
import useDuctedBundleAdd from 'hooks/use-ducted-bundle-add';
import type { DuctedBundleSlot } from 'utils/ducted-bundle-picks-types';
import { DUCTED_BUNDLE_MIN_PAIRS } from 'utils/ducted-bundle-picks';
import { formatBtuOutput } from 'utils/ducted-zone-performance';

type DuctedBundleSlotCardProps = Readonly<{
  slot: DuctedBundleSlot;
  disabled: boolean;
  onInspect?: (slot: DuctedBundleSlot) => void;
}>;

function DuctedBundleSlotCard({
  slot,
  disabled,
  onInspect
}: DuctedBundleSlotCardProps) {
  const dispatch = useAppDispatch();
  const { pick, pairName, context } = slot;
  return (
    <div
      data-testid='ducted-bundle-slot'
      className='flex min-w-0 flex-1 items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2'
    >
      {pick ? (
        <CheckCircleIcon className='h-5 w-5 shrink-0 text-green-600' />
      ) : (
        <span className='h-5 w-5 shrink-0 rounded-full border-2 border-dashed border-gray-300' />
      )}
      <div className='flex min-w-0 grow flex-col'>
        <span className='truncate text-xs font-semibold uppercase tracking-wider text-gray-500'>
          {pairName} · {formatBtuOutput(context.loads.cooling)} cooling
        </span>
        {pick ? (
          <button
            type='button'
            className='truncate text-left text-sm font-medium text-gray-900 hover:text-blue-600'
            onClick={() => onInspect?.(slot)}
          >
            {pick.selection.title}
          </button>
        ) : (
          <span className='text-sm text-gray-500'>Choose a system</span>
        )}
      </div>
      {pick && (
        <button
          type='button'
          aria-label={`Clear selection for ${pairName}`}
          className='shrink-0 text-gray-400 hover:text-gray-600 disabled:cursor-not-allowed'
          disabled={disabled}
          onClick={() => dispatch(clearDuctedPairPick(slot.systemUniqueId))}
        >
          <XMarkIcon className='h-4 w-4' />
        </button>
      )}
    </div>
  );
}

type DuctedBundleBarProps = Readonly<{
  onInspectSlot?: (slot: DuctedBundleSlot) => void;
}>;

export default function DuctedBundleBar({
  onInspectSlot
}: DuctedBundleBarProps) {
  const { slots, ready, isAdding, beginAdd, accessorySelection } =
    useDuctedBundleAdd();
  if (slots.length < DUCTED_BUNDLE_MIN_PAIRS) return null;

  return (
    <div
      data-testid='ducted-bundle-bar'
      className='flex flex-col gap-2 rounded-lg border border-blue-100 bg-blue-50 p-3'
    >
      <span className='text-xs text-gray-600'>
        Pick one system for each outdoor unit on your System Design, then add
        the set to your comparison.
      </span>
      <div className='flex flex-wrap items-stretch gap-2'>
        {slots.map((slot) => (
          <DuctedBundleSlotCard
            key={slot.systemUniqueId}
            slot={slot}
            disabled={isAdding}
            onInspect={onInspectSlot}
          />
        ))}
        <div className='flex shrink-0 items-center'>
          <Button
            color='primary'
            text={isAdding ? 'Adding…' : 'Add to Comparison'}
            loading={isAdding}
            disabled={!ready || isAdding}
            tooltip={
              ready ? undefined : 'Select a system for every outdoor unit first'
            }
            onClick={beginAdd}
          />
        </div>
      </div>
      <AccessorySelectionModal
        openState={accessorySelection.openState}
        combinationsWithChoices={accessorySelection.combinationsWithChoices}
        systemCount={accessorySelection.systemCount}
        onConfirm={accessorySelection.onConfirm}
      />
    </div>
  );
}
